/**
 * FEED-09 — "come back here after signing in", and the one extra fact that
 * makes returning by a history step back safe.
 *
 * `redirect_after_auth` alone says WHERE to return. It does not say that the
 * destination is the history entry the user is standing one step above, and on
 * the native OAuth path that is the difference between a working Back button
 * and the wrong screen:
 *
 *   - PropertyDetails writes the key when it merely OPENS the auth prompt.
 *     Dismissing that prompt does not clear it, so the key can outlive the
 *     visit that wrote it.
 *   - Pushing /auth and then backing out of it leaves the key set too.
 *
 * Either way the user can browse on and sign in from somewhere else entirely,
 * with a key still pointing at a listing that is no longer underneath them.
 *
 * So the writer also records the history depth it wrote at. Exactly one entry
 * is pushed between that moment and the callback — the /auth push, which the
 * native round trip then REPLACES with /auth/callback — so
 * `depth + 1 === history.length` holds if and only if the stored destination is
 * still the entry directly beneath. Anything else (a back-out, a truncated
 * forward branch, an unrelated push in between) breaks the equality and the
 * caller falls back to navigating to the destination outright, exactly as
 * before this existed.
 *
 * `sessionStorage`, deliberately, where the destination itself lives in
 * `localStorage`: a depth is only meaningful inside the history session that
 * produced it, and one carried over from a previous session would be a number
 * with nothing behind it. The record also carries the path it describes, so a
 * destination stored by some other writer (DeleteAccountFlow stores
 * `/delete-account?reauthed=1`, which is deliberately NOT "where you were")
 * can never be matched by a leftover record.
 */

const RETURN_TO_KEY = 'redirect_after_auth';
const RETURN_WITNESS_KEY = 'afriqahome_return_witness';

interface ReturnWitness {
  /** The destination this record vouches for. */
  to: string;
  /** `window.history.length` at the moment the destination was stored. */
  depth: number;
}

/**
 * Store "return here after signing in", together with the witness above.
 *
 * Call this instead of writing `redirect_after_auth` directly whenever the
 * screen being left IS the destination. Writers that store somewhere else —
 * DeleteAccountFlow — must keep using the plain key, so that no step back is
 * ever attempted on their behalf.
 */
export function rememberReturnAfterAuth(destination: string): void {
  if (typeof window === 'undefined') return;

  localStorage.setItem(RETURN_TO_KEY, destination);
  try {
    const witness: ReturnWitness = { to: destination, depth: window.history.length };
    sessionStorage.setItem(RETURN_WITNESS_KEY, JSON.stringify(witness));
  } catch {
    // No sessionStorage (private mode, quota) — the destination is still
    // stored, and the caller simply navigates to it rather than stepping back.
  }
}

/**
 * True when `destination` is provably the history entry directly beneath the
 * current one, so going back one entry lands on it.
 *
 * Consumes the witness either way: it describes one hand-off to /auth and must
 * never be read twice.
 */
export function returnIsOneStepBack(destination: string): boolean {
  if (typeof window === 'undefined') return false;

  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(RETURN_WITNESS_KEY);
    sessionStorage.removeItem(RETURN_WITNESS_KEY);
  } catch {
    return false;
  }
  if (!raw) return false;

  let witness: ReturnWitness;
  try {
    witness = JSON.parse(raw) as ReturnWitness;
  } catch {
    return false;
  }

  return (
    witness?.to === destination &&
    typeof witness.depth === 'number' &&
    witness.depth + 1 === window.history.length
  );
}
